import { appRootElement } from "app-root-element";
import auth0 from "auth0-js";
import axios from "axios";

import { createResponseError } from "@/api/api";
import { AuthContextValue } from "@/auth/auth-context";
import { env } from "@/env/env";
import { User } from "@/types/proto/exabel/user/user_messages";

// Send a password reset email to an Auth0 user.
export const changePassword = (email: string) => {
  return new Promise<void>((resolve, reject) => {
    const params = {
      connection: realm(),
      email,
    };

    createClient().changePassword(params, function (error, res) {
      if (error) {
        return reject(error);
      }

      resolve();
    });
  });
};

// Fetch current user
export const fetchUser = async () => {
  // Do not log out if user is not logged in.
  return await axios
    .get(`${env().API_ORIGIN}/v1/users/current`, { withCredentials: true })
    .then((res) => res.data as User)
    .catch((error: any) => {
      if (error.response?.status === 401) {
        // User not logged in.
        return {} as User;
      }
      throw createResponseError(error);
    });
};

// Redirect to the login URL
export const login = () => {
  const returnUrl = new URL(window.location.href);
  returnUrl.searchParams.delete("error");
  returnUrl.searchParams.delete("error_description");
  window.location.replace(
    `${env().API_ORIGIN}/auth/v1/login?clientId=${
      env().AUTH0_CLIENT_ID
    }&returnUrl=${encodeURIComponent(returnUrl.toString())}`
  );
};

// Redirect to the logout URL.
// Returns a never-resolving promise while waiting for the redirect.
// Unmounts the React app while waiting to avoid transient error pages.
export const logout = async (redirectPath?: string) => {
  // Get the root element and unmount using the new API
  if (appRootElement && (appRootElement as any)._reactRootContainer) {
    (appRootElement as any)._reactRootContainer.unmount();
  }
  if (env().MIXPANEL_ENABLED) {
    const mixpanel = (await import("mixpanel-browser")).default;

    mixpanel.reset();
  }

  const returnUrl = encodeURIComponent(
    window.location.origin + (redirectPath || "/")
  );
  window.location.replace(
    `${env().API_ORIGIN}/auth/v1/logout?clientId=${
      env().AUTH0_CLIENT_ID
    }&returnUrl=${returnUrl}`
  );

  return new Promise(() => {});
};

// Check if the current user is blocked or expired, and in that case logout
// the user (to make it possible to log in as a different user), and redirect
// to login error message.
export const validUser = (auth: AuthContextValue) => {
  if (auth.isBlocked || auth.isExpired) {
    const url = new URL(window.location.href);
    url.searchParams.append(
      "error",
      `User is ${auth.isBlocked ? "blocked" : "expired"}.`
    );
    void logout(url.pathname + url.search);
    return false;
  }
  return true;
};

// Get the URL to an Auth0 user profile.
export const userURL = (auth0Uid: string) => {
  const urlSafeEncodedID = btoa(encodeURIComponent(auth0Uid))
    .replace(/\//g, "_")
    .replace(/\+/g, "-")
    .replace(/=/g, "");
  return `https://manage.auth0.com/dashboard/eu/${tenant()}/users/${urlSafeEncodedID}`;
};

// Auth0 web client.
const createClient = () => {
  return new auth0.WebAuth({
    domain: domain(),
    clientID: env().AUTH0_CLIENT_ID,
  });
};

const realm = () => "Username-Password-Authentication";
const domain = () => env().AUTH0_DOMAIN;
const tenant = () => env().AUTH0_TENANT;
